import { acceptHMRUpdate, defineStore } from 'pinia'
import { VisitsApiFp } from '~/services/apiClientGps'
import type { GeoFenceVisitWithObjectsLinked } from '~/services/apiClientGps'
import { calculateDuration, formatTime } from '~/utils/dateTimeUtils'

export interface VisitDetails {
  deviceName?: string
  externalDeviceId?: string
  container?: string
  containerType?: string
  customer?: string
  terminal?: string
  geofenceName?: string
  locationName?: string
  driver?: string
  driverId?: string
  appointment?: string
  carrier?: string
  lineTime?: string
  entryTime?: string
  exitTime?: string
  totalTime?: string
  hourlyRate: number
  totalCharge?: number
  isNotBillable: boolean
  licensePlate?: string
  driverName?: string
}

interface VisitFilters {
  deviceIds?: string
  externalDeviceIds?: string
  locationIds?: string
}

const DEFAULT_LIMIT = 20
const DEFAULT_HOURLY_RATE = 75

const defaultVisitDetailsState: VisitDetails = {
  externalDeviceId: undefined,
  container: undefined,
  containerType: undefined,
  customer: undefined,
  terminal: undefined,
  geofenceName: undefined,
  locationName: undefined,
  driver: undefined,
  driverId: undefined,
  appointment: undefined,
  carrier: undefined,
  lineTime: '---',
  entryTime: '---',
  exitTime: '---',
  totalTime: '0m',
  hourlyRate: DEFAULT_HOURLY_RATE,
  totalCharge: 0,
  isNotBillable: false,
}

export const useVisitsStore = defineStore('visits', () => {
  // List State
  const visits = ref<GeoFenceVisitWithObjectsLinked[]>([])
  const loading = ref(false)
  const hasMore = ref(true)
  const nextCursor = ref<string | undefined>(undefined)
  const limit = ref(DEFAULT_LIMIT)
  const filters = ref<VisitFilters>({})
  const error = ref<string | null>(null)

  // Selected Visit State
  const selectedVisit = ref<GeoFenceVisitWithObjectsLinked | null>(null)
  const visitDetails = ref<VisitDetails>({ ...defaultVisitDetailsState })

  // List Getters
  const totalVisits = computed(() => visits.value.length)
  const isLoading = computed(() => loading.value)
  const canLoadMore = computed(() => hasMore.value && !loading.value)

  // Selected Visit Getters
  const isSelected = computed(() => selectedVisit.value !== null)
  const status = computed(() => {
    if (!selectedVisit.value) return null
    return selectedVisit.value.departure ? 'completed' : 'in_progress'
  })

  const calculateCharges = (
    arrival: string | Date,
    departure: string | Date | undefined,
    rate: number
  ): number => {
    if (!departure || rate === 0) return 0

    const start = new Date(arrival).getTime()
    const end = new Date(departure).getTime()
    const diff = Math.max(0, end - start)

    const hours = diff / (1000 * 60 * 60)
    return Math.round(hours * rate * 100) / 100
  }

  // List Actions
  async function loadMore() {
    if (!canLoadMore.value) return

    try {
      loading.value = true
      error.value = null

      const visitsApi = VisitsApiFp()
      const response = await visitsApi
        .getVisitsVisitsGet(
          nextCursor.value, // beforeCursor
          limit.value, // limit
          filters.value.deviceIds,
          filters.value.externalDeviceIds,
          filters.value.locationIds
        )
        .then((request) => request())

      visits.value = [...visits.value, ...response.data.items]
      nextCursor.value = response.data.next_cursor
      hasMore.value = response.data.has_more
    } catch (err: any) {
      console.error('Error loading visits:', err)
      error.value =
        err.response?.data?.detail || err.message || 'Failed to load visits'
      hasMore.value = false
    } finally {
      loading.value = false
    }
  }

  function reset() {
    visits.value = []
    nextCursor.value = undefined
    hasMore.value = true
    error.value = null
    clearSelection()
  }

  function setFilters(newFilters: VisitFilters) {
    const hasChanges =
      newFilters.deviceIds !== filters.value.deviceIds ||
      newFilters.externalDeviceIds !== filters.value.externalDeviceIds ||
      newFilters.locationIds !== filters.value.locationIds

    if (hasChanges) {
      filters.value = {
        deviceIds: newFilters.deviceIds || undefined,
        externalDeviceIds: newFilters.externalDeviceIds || undefined,
        locationIds: newFilters.locationIds || undefined,
      }
      reset()
    }
  }

  // Selected Visit Actions
  function selectVisit(visit: GeoFenceVisitWithObjectsLinked) {
    selectedVisit.value = visit

    const geofenceName = visit.geo_fence?.name || '---'
    const locationName =
      visit.geo_fence?.location?.name.toLocaleUpperCase() || '---'

    const currentHourlyRate = (visit as any).hourly_rate || DEFAULT_HOURLY_RATE

    visitDetails.value = {
      ...defaultVisitDetailsState,
      deviceName: visit.tracking_device?.name,
      externalDeviceId: visit.tracking_device?.external_id,
      licensePlate: visit.tracking_device?.license_plate,
      driverName: visit.tracking_device?.drivers?.[0]?.name,
      terminal: locationName,
      geofenceName,
      locationName,
      lineTime: formatTime(visit.line),
      entryTime: formatTime(visit.arrival),
      exitTime: formatTime(visit.departure),
      hourlyRate: currentHourlyRate,
      totalCharge: calculateCharges(
        visit.arrival,
        visit.departure,
        currentHourlyRate
      ),
      totalTime: calculateDuration(visit.arrival, visit.departure),
    }
  }

  function clearSelection() {
    selectedVisit.value = null
    visitDetails.value = { ...defaultVisitDetailsState }
  }

  function setNotBillable(value: boolean) {
    if (visitDetails.value) {
      visitDetails.value.isNotBillable = value
      if (value) {
        visitDetails.value.totalCharge = 0
      } else {
        if (selectedVisit.value) {
          const currentHourlyRate =
            (selectedVisit.value as any).hourly_rate || DEFAULT_HOURLY_RATE
          visitDetails.value.totalCharge = calculateCharges(
            selectedVisit.value.arrival,
            selectedVisit.value.departure,
            currentHourlyRate
          )
        }
      }
    }
  }

  return {
    // State
    visits,
    loading,
    hasMore,
    nextCursor,
    limit,
    filters,
    error,
    selectedVisit,
    visitDetails,

    // Getters
    totalVisits,
    isLoading,
    canLoadMore,
    isSelected,
    status,

    // Actions
    loadMore,
    reset,
    setFilters,
    selectVisit,
    clearSelection,
    setNotBillable,

    // Utils
    calculateCharges,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useVisitsStore, import.meta.hot))
}
